import './PopupSimpleContent.scss';

import classNames from 'classnames';
import {
  Button,
  HTMLRenderer,
  ImgWithAlt,
  PetDNALink,
} from 'petdna-lib-uicomponents/components';
import { isAbsoluteUrl } from 'petdna-lib-uicomponents/helpers';
import {
  useGetCheckoutUrl,
  useGetKitPrices,
} from 'petdna-lib-uicomponents/hooks';
import { RenderType } from 'petdna-lib-uicomponents/types';
import React, {
  FC, useCallback, useId, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  EPopupButtonAction,
  IPopup,
  IPopupSimpleContent,
  TPopupContent,
  TPopupSetting,
} from 'stores/types/Popups';

import PopupDisplayedNameInput from './PopupDisplayedNameInput/PopupDisplayedNameInput';
import PopupPetLocationInput from './PopupPetLocationInput/PopupPetLocationInput';

interface IPopupSimpleContentProps {
  popup: IPopup<TPopupSetting, IPopupSimpleContent>;
  onClose: () => void;
  currentNumber?: number;
  maxNumber?: number;
}

const PopupInputMap: {
  [key in EPopupButtonAction]: FC<{
    formId: string;
    popup: IPopup<TPopupSetting, TPopupContent>;
    onClose: () => void;
  }>;
} = {
  [EPopupButtonAction.UPDATE_PET_LOCATION]: PopupPetLocationInput,
  [EPopupButtonAction.UPDATE_DISPLAYED_USERNAME]: PopupDisplayedNameInput,
};

const PopupSimpleContent: FC<IPopupSimpleContentProps> = ({
  popup,
  onClose,
  currentNumber,
  maxNumber,
}) => {
  const navigate = useNavigate();
  const unknownFormId = useId();
  const { t } = useTranslation();

  const kitPrices = useMemo(
    () => (popup.content.kitPrice ? [popup.content.kitPrice] : null),
    [popup],
  );

  const [selectedItems] = useGetKitPrices(kitPrices);
  const { checkoutUrl } = useGetCheckoutUrl(selectedItems);

  const handleButtonClick = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    onClose();
    const url = checkoutUrl || popup.content.button?.externalUrl;
    if (!url) return;
    const isAbsolute = isAbsoluteUrl(url);
    if (isAbsolute) {
      window.location.href = url;
    } else {
      navigate(url);
    }
  }, [popup, checkoutUrl, onClose, navigate]);

  const isIcon = useMemo(
    () => popup.content.icon?.url && !popup.content.imageWithAlt,
    [popup],
  );

  const showPopupNumber = useMemo(
    () => Boolean(currentNumber) && Boolean(maxNumber),
    [currentNumber, maxNumber],
  );

  const PopupInputComponent = useMemo(
    // eslint-disable-next-line no-underscore-dangle
    () => (popup.content.buttonAction ? PopupInputMap[popup.content.buttonAction] : null),
    [popup.content.buttonAction],
  );

  return (
    <div className="popup-simple-content">
      <div className="modal-body">
        {showPopupNumber && (
          <div className="popup-simple-content__task-number">
            {t('popupSimpleContent.task', { currentNumber, maxNumber })}
          </div>
        )}
        {popup.content.closeButton && (
          <button
            type="button"
            className="popup-simple-content__close-button"
            onClick={onClose}
            aria-label={t('close')}
          >
            <span className="icon icon-close" />
          </button>
        )}
        <div
          className={classNames(
            'popup-simple-content__image-wrapper',
            isIcon ? 'popup-simple-content__icon' : '',
          )}
        >
          {isIcon ? (
            <img
              width={popup.content.icon?.width}
              height={popup.content.icon?.height}
              src={popup.content.icon?.url}
              alt=""
            />
          ) : (
            <ImgWithAlt imageWithAlt={popup.content.imageWithAlt} />
          )}
        </div>
        <HTMLRenderer
          type={RenderType.TITLE}
          contentToRender={popup.content.title}
          className="popup-simple-content__title"
          style={{
            color: popup.content.titleColor?.hex,
          }}
        />
        <HTMLRenderer
          type={RenderType.TEXT}
          contentToRender={popup.content.text}
          className="popup-simple-content__text"
          style={{
            color: popup.content.textColor?.hex,
          }}
        />
        {popup.content.learnMoreLink && popup.content.learnMoreLabel && (
          <div className="popup-simple-content__learn-more">
            <PetDNALink
              className="popup-simple-content__learn-more__link"
              style={{ color: popup.content.learnMoreLabelColor?.hex }}
              href={popup.content.learnMoreLink}
              onClick={onClose}
            >
              {popup.content.learnMoreLabel}
            </PetDNALink>
          </div>
        )}
        {popup.content.inputField && PopupInputComponent && (
          <PopupInputComponent
            formId={unknownFormId}
            popup={popup}
            onClose={onClose}
          />
        )}
      </div>
      <div className={classNames('modal-footer', isIcon ? 'border-top' : '')}>
        {popup.content.closeButton && (
          <button
            type="button"
            className={classNames(
              'btn popup-simple-content__close',
              !popup.content.button ? 'full-width' : '',
            )}
            onClick={onClose}
          >
            {popup.content.closeButtonText || t('close')}
          </button>
        )}
        {popup.content.button && (
          <Button
            className={classNames(
              'btn popup-simple-content__action',
              !popup.content.closeButton ? 'full-width' : '',
            )}
            type={popup.content.inputField ? 'submit' : 'button'}
            onClick={popup.content.inputField ? undefined : handleButtonClick}
            {...popup.content.button}
            externalUrl={checkoutUrl || undefined}
            form={unknownFormId}
          />
        )}
      </div>
    </div>
  );
};

export default PopupSimpleContent;
